/**
 * R5 — RP60 → RP62, RP65 (revue produit 2026-09, D26 A) : la frise par lot dans Marchés.
 *
 * Pour chaque lot : Préparé → Consulté → Attribué → Signé → En cours → Réceptionné
 * (réceptionné = PV de réception signé couvrant le lot). Depuis la frise :
 * - la consultation (DCE, déplacée de Conception vers Marchés) ;
 * - le comparatif des offres et le rapport d'analyse (PDF), AVANT et APRÈS l'attribution ;
 * - la demande d'avis à l'AMO (dès la première offre ; l'AMO lit les prix après la date limite, D28) ;
 * - pour l'AMO : donner son avis sur l'attribution.
 * A6 (P2#6, #7, #16, P3#10, P5#6) : les lots de la DPGF apparaissent tout de suite (« Préparé »),
 * un seul numéro (« Lot 03 »), un seul bouton de consultation, la date limite visible, et les
 * titulaires sans accès signalés avec « Inviter l'entreprise ».
 * B6 (P2#20) — l'étape « Signé » est cliquable : « Déposer le marché signé » (scan) ou
 * « Envoyer à la signature » (signature électronique existante, par code SMS). Elle passe
 * au vert dans les deux cas ; pendant la signature elle affiche « En signature (x/n) ».
 * Source : GET /api/chantiers/{id}/marches/frise.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  CheckCircle, Circle, MinusCircle, Trophy, FilePdf, Megaphone, PaperPlaneTilt, UserPlus, Warning,
  PenNib, UploadSimple, Hourglass,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import DCEComparatifDialog from './DCEComparatifDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtEur = (n) => (n === null || n === undefined ? '—'
  : `${Number(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} €`);
const fmtDate = (d) => {
  if (!d) return '';
  try { return new Date(d.length === 10 ? `${d}T12:00:00` : d).toLocaleDateString('fr-FR'); } catch { return ''; }
};
// P2#16 — un seul numéro : « Lot 03 » (place dans la DPGF) ou le code saisi
const libNumero = (n) => (n ? (/^\d+$/.test(String(n)) ? `Lot ${n}` : n) : '');

function Etape({ etape, courante, onClic }) {
  const fait = etape.etat === 'fait';
  const saute = etape.etat === 'saute';
  const enSignature = !!etape.en_signature;
  const Icon = fait ? CheckCircle : (enSignature ? Hourglass : (saute ? MinusCircle : Circle));
  const couleur = fait ? 'text-[#2D6A4F]' : (enSignature ? 'text-[#D4880F]' : (courante ? 'text-[#C75B39]' : 'text-[#B5B5B0]'));
  const contenu = (
    <>
      <Icon size={20} weight={fait ? 'fill' : (courante || enSignature ? 'bold' : 'regular')} className={couleur} />
      <span className={`text-[11px] mt-1 ${enSignature ? 'font-bold text-[#D4880F]' : courante ? 'font-bold text-[#C75B39]' : fait ? 'font-semibold text-[#2C2C2C]' : 'text-[#9A9A9A]'}`}>
        {etape.libelle}
      </span>
      {(fait || enSignature) && (etape.detail || etape.date) && (
        <span className="text-[10px] text-[#6B6B6B] leading-tight truncate max-w-full">
          {etape.detail || fmtDate(etape.date)}
        </span>
      )}
      {onClic && !fait && !enSignature && (
        <span className="text-[10px] text-[#C75B39] underline leading-tight">Déposer ou faire signer</span>
      )}
    </>
  );
  return (
    <li className="flex-1 min-w-[92px] flex flex-col items-center text-center px-1" data-testid={`frise-etape-${etape.cle}`}
      title={saute ? 'Étape non utilisée pour ce lot' : (etape.detail || etape.libelle)}>
      {onClic ? (
        <button type="button" onClick={onClic} className="flex flex-col items-center hover:opacity-80"
          data-testid={`frise-signe-ouvrir-${etape.cle}`}>
          {contenu}
        </button>
      ) : contenu}
    </li>
  );
}

const lireBase64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result || ''));
  r.onerror = reject;
  r.readAsDataURL(file);
});

/**
 * B6 (P2#20) — Étape « Signé » : déposer le marché signé, ou l'envoyer à la signature
 * électronique (même circuit que les comptes rendus et les PV : code par SMS).
 */
function MarcheSigneDialog({ chantierId, lot, onClose, onFait }) {
  const [mode, setMode] = useState(null); // 'deposer' | 'signature'
  const [fichier, setFichier] = useState(null);
  const [signataires, setSignataires] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  const sig = lot?.signature;

  useEffect(() => {
    if (!lot || mode !== 'signature') return;
    axios.get(`${API_URL}/api/chantiers/${chantierId}/marches/${lot.id}/signataires-proposes`, { withCredentials: true })
      .then(({ data }) => setSignataires(data.signataires || []))
      .catch(() => setSignataires([]));
  }, [lot, mode, chantierId]);

  const majSignataire = (i, k, v) => setSignataires((l) => l.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  const deposer = async () => {
    if (!fichier) return;
    setEnvoi(true);
    try {
      const contenu = await lireBase64(fichier);
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/marches/${lot.id}/documents`,
        { filename: fichier.name, content_base64: contenu, type: 'marche_signe' }, { withCredentials: true });
      toast.success('Marché signé déposé : l\'étape « Signé » est faite');
      onFait();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Le marché signé n\'a pas été déposé');
    } finally { setEnvoi(false); }
  };

  const envoyerSignature = async () => {
    if (!fichier) return;
    const incomplet = signataires.find((s) => !s.nom?.trim() || !s.email?.trim() || !s.telephone?.trim());
    if (!signataires.length || incomplet) {
      toast.error('Nom, e-mail et téléphone sont nécessaires pour chaque signataire (le code arrive par SMS).');
      return;
    }
    setEnvoi(true);
    try {
      const contenu = await lireBase64(fichier);
      const { data: doc } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/marches/${lot.id}/marche-a-signer`,
        { filename: fichier.name, content_base64: contenu }, { withCredentials: true });
      await axios.post(`${API_URL}/api/chantiers/${chantierId}/signatures`, {
        document_type: 'document',
        document_id: doc.document_id,
        titre: `Marché — ${libNumero(lot.numero)} ${lot.nom}`.trim(),
        signataires: signataires.map((s) => ({ nom: s.nom, email: s.email, telephone: s.telephone, role: s.role, obligatoire: true })),
      }, { withCredentials: true });
      toast.success('Marché envoyé à la signature : chaque signataire reçoit un lien par e-mail');
      onFait();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'L\'envoi à la signature a échoué');
    } finally { setEnvoi(false); }
  };

  return (
    <Dialog open={!!lot} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-xl" data-testid="marche-signe-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>
            Marché signé — {libNumero(lot?.numero)} {lot?.nom}
          </DialogTitle>
        </DialogHeader>
        {sig && sig.statut === 'en_cours' && (
          <div className="border border-[#D4880F] bg-[#FFFBEB] px-3 py-2 text-sm text-[#6B4A24]" data-testid="marche-signe-en-cours">
            En signature depuis le {fmtDate(sig.demande_le)} : {sig.nb_signes}/{sig.nb_signataires} signature{sig.nb_signataires > 1 ? 's' : ''}.
            <ul className="mt-1 text-xs">
              {sig.signataires.map((s, i) => (
                <li key={i}>{s.nom} ({s.role}) : {s.statut === 'signe' ? 'a signé' : s.statut === 'refuse' ? 'a refusé' : 'en attente'}</li>
              ))}
            </ul>
          </div>
        )}
        {!mode && (
          <div className="grid sm:grid-cols-2 gap-3">
            <button type="button" onClick={() => setMode('deposer')} data-testid="marche-signe-deposer"
              className="border border-[#E0E0DB] p-4 text-left hover:border-[#2D6A4F]">
              <UploadSimple size={20} className="text-[#2D6A4F]" />
              <p className="font-semibold text-sm mt-2">Déposer le marché signé</p>
              <p className="text-xs text-[#6B6B6B]">Le marché a été signé sur papier : déposez le scan (PDF).</p>
            </button>
            <button type="button" onClick={() => setMode('signature')} data-testid="marche-signe-envoyer"
              className="border border-[#E0E0DB] p-4 text-left hover:border-[#C75B39]">
              <PenNib size={20} className="text-[#C75B39]" />
              <p className="font-semibold text-sm mt-2">Envoyer à la signature</p>
              <p className="text-xs text-[#6B6B6B]">Signature électronique par code SMS : l'entreprise et le maître d'ouvrage signent en ligne.</p>
            </button>
          </div>
        )}
        {mode && (
          <div className="space-y-3">
            <label className="block text-sm">
              <span className="block text-[#6B6B6B] mb-1">
                {mode === 'deposer' ? 'Scan du marché signé (PDF)' : 'Marché à signer (PDF)'}
              </span>
              <input type="file" accept=".pdf,application/pdf" onChange={(e) => setFichier(e.target.files?.[0] || null)}
                data-testid="marche-signe-fichier" />
            </label>
            {mode === 'signature' && (
              <div className="space-y-2" data-testid="marche-signe-signataires">
                <p className="text-sm font-medium">Signataires</p>
                {signataires.map((s, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input value={s.nom} onChange={(e) => majSignataire(i, 'nom', e.target.value)} placeholder={s.role || 'Nom'} className="rounded-none text-xs" />
                    <Input value={s.email} onChange={(e) => majSignataire(i, 'email', e.target.value)} placeholder="E-mail" className="rounded-none text-xs" />
                    <Input value={s.telephone} onChange={(e) => majSignataire(i, 'telephone', e.target.value)} placeholder="Téléphone (code SMS)" className="rounded-none text-xs" />
                  </div>
                ))}
                <p className="text-xs text-[#6B6B6B]">L'étape « Signé » passe au vert quand tous ont signé.</p>
              </div>
            )}
            <div className="flex justify-between gap-2">
              <Button variant="ghost" className="rounded-none" onClick={() => { setMode(null); setFichier(null); }}>Retour</Button>
              {mode === 'deposer' ? (
                <Button className="rounded-none bg-[#2D6A4F] hover:bg-[#225538]" disabled={!fichier || envoi} onClick={deposer}
                  data-testid="marche-signe-deposer-valider">Déposer le marché signé</Button>
              ) : (
                <Button className="rounded-none bg-[#C75B39] hover:bg-[#D97B5C]" disabled={!fichier || envoi} onClick={envoyerSignature}
                  data-testid="marche-signe-envoyer-valider">Envoyer à la signature</Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * props :
 * - chantierId, roleProjet
 * - canWrite : rôles MOE (ouvrir la consultation)
 * - canDemanderAvis : architecte / chef de projet
 * - onOuvrirConsultation(lot) : affiche la consultation (DCE) dans Marchés
 * - refreshKey : recharger après une attribution / un avenant
 */
export default function MarchesFrise({
  chantierId, roleProjet, canWrite = false, canDemanderAvis = false, onOuvrirConsultation, refreshKey = 0, onChanged,
  actionsLot, // A5 / P4#8 — actions ajoutées sur la carte du lot (ex. « Nouvel avenant »)
  onLots,
}) {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparatif, setComparatif] = useState(null); // { dceId, dceStatut, lotId }
  const [avisCible, setAvisCible] = useState(null);   // lot pour la demande d'avis
  const [avisMessage, setAvisMessage] = useState('');
  const [envoi, setEnvoi] = useState(false);
  // A6 (P3#10, P5#6) — inviter une entreprise titulaire sans accès
  const [invCible, setInvCible] = useState(null);
  const [invEmail, setInvEmail] = useState('');
  const [invResultat, setInvResultat] = useState(null);
  // B6 (P2#20) — étape « Signé »
  const [signeCible, setSigneCible] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/chantiers/${chantierId}/marches/frise`, { withCredentials: true, silent403: true });
      setLots(data.lots || []);
      onLots?.((data.lots || []).length);
    } catch (err) {
      onLots?.(0);  // A6 — la liste manuelle reste disponible pour les rôles sans frise
      if (err.response?.status !== 403) toast.error(err.response?.data?.detail || 'Erreur de chargement de la frise');
    } finally { setLoading(false); }
  }, [chantierId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const envoyerDemandeAvis = async () => {
    if (!avisCible) return;
    setEnvoi(true);
    try {
      await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dce/${avisCible.dce_id}/demande-avis`,
        { lot_ids: [avisCible.dpgf_lot_id], message: avisMessage },
        { withCredentials: true },
      );
      toast.success("Demande d'avis envoyée à l'AMO");
      setAvisCible(null);
      setAvisMessage('');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la demande d'avis");
    } finally { setEnvoi(false); }
  };

  const inviterEntreprise = async () => {
    if (!invCible) return;
    setEnvoi(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/marches/${invCible.id}/inviter-entreprise`,
        { email: invEmail }, { withCredentials: true });
      setInvResultat(data);
      toast.success(data.message);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invitation impossible");
    } finally { setEnvoi(false); }
  };

  if (loading) return <div className="p-4 text-sm text-[#9A9A9A]">Chargement de la frise des lots…</div>;
  if (!lots.length) return null;

  const estAmo = roleProjet === 'amo';
  const sansAcces = lots.filter((l) => l.sans_acces);
  const consultationEnCours = lots.find((l) => l.dce_id && ['EN_PREPARATION', 'EN_COURS'].includes(l.dce_statut));
  const dateLimite = (consultationEnCours || lots.find((l) => l.date_limite_reponse) || {}).date_limite_reponse;
  const aConsulter = lots.some((l) => !l.dce_id);

  return (
    <div className="space-y-3 mb-6" data-testid="marches-frise">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-bold uppercase tracking-wider text-[#6B6B6B] flex-1">Suivi par lot</h3>
        {dateLimite && (
          <span className="text-xs text-[#1E3A5F] bg-[#E3F2FD] px-2 py-1" data-testid="frise-date-limite">
            Date limite des offres : <strong>{fmtDate(dateLimite)}</strong>
          </span>
        )}
        {/* P2#7 — un seul bouton de consultation */}
        {canWrite && (
          <Button size="sm" className="rounded-none text-xs gap-1 bg-[#C75B39] hover:bg-[#D97B5C]"
            onClick={() => onOuvrirConsultation?.()} data-testid="frise-consultation">
            <PaperPlaneTilt size={12} /> {consultationEnCours ? 'Suivre la consultation' : (aConsulter ? 'Préparer la consultation' : 'Consultation')}
          </Button>
        )}
      </div>
      {/* P5#6 — titulaires sans accès */}
      {canWrite && sansAcces.length > 0 && (
        <div className="flex items-start gap-2 border border-[#D4880F] bg-[#FFFBEB] px-3 py-2 text-sm text-[#6B4A24]" data-testid="frise-sans-acces">
          <Warning size={16} className="mt-0.5 flex-shrink-0" />
          <span>
            {sansAcces.length === 1
              ? `1 entreprise titulaire n'a pas d'accès (${sansAcces[0].entreprise_nom}) : elle ne voit ni son marché, ni ses réserves.`
              : `${sansAcces.length} entreprises titulaires n'ont pas d'accès (${sansAcces.map((l) => l.entreprise_nom).join(', ')}) : elles ne voient ni leur marché, ni leurs réserves.`}
            {' '}Utilisez « Inviter l'entreprise » sur le lot.
          </span>
        </div>
      )}
      {lots.map((lot) => {
        const aDesOffres = lot.dce_id && (lot.nb_offres > 0 || lot.nb_offres === null);
        const numero = libNumero(lot.numero);
        return (
          <div key={lot.id} className="border border-[#E0E0DB] bg-white" data-testid={`frise-lot-${lot.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2 px-4 pt-3">
              <div className="min-w-0">
                <p className="font-semibold text-[#2C2C2C]">
                  <span className="text-[#1E3A5F] font-bold">{numero}</span>{numero ? ' — ' : ''}{lot.nom}
                  {lot.corps_etat && <span className="ml-2 text-[11px] text-[#9A9A9A] font-normal">{lot.corps_etat}</span>}
                </p>
                <p className="text-xs text-[#6B6B6B]">
                  {lot.virtuel ? 'Lot de la DPGF, pas encore consulté' : (lot.entreprise_nom || 'Entreprise non attribuée')}
                  {lot.sans_acces && <span className="ml-1 text-[10px] font-bold uppercase text-[#B45309]">· sans accès à Kroquis</span>}
                  {lot.dce_id && (
                    <> · {lot.nb_invites} invitée{lot.nb_invites > 1 ? 's' : ''}
                      {lot.offres_masquees
                        ? ' · offres visibles après la date limite'
                        : ` · ${lot.nb_offres} offre${lot.nb_offres > 1 ? 's' : ''}`}
                    </>
                  )}
                </p>
              </div>
              {/* FIN-09 — marché initial + avenants acceptés, affichés séparément */}
              {lot.marche_initial_ht != null && (
                <div className="text-right text-xs" data-testid={`frise-montants-${lot.id}`}>
                  <div className="text-[#6B6B6B]">Marché initial <span className="font-semibold text-[#2C2C2C]">{fmtEur(lot.marche_initial_ht)}</span></div>
                  {lot.avenants_acceptes_ht !== 0 && (
                    <>
                      <div className="text-[#D4880F]">Avenants acceptés {lot.avenants_acceptes_ht > 0 ? '+' : ''}{fmtEur(lot.avenants_acceptes_ht)}</div>
                      <div className="font-bold text-[#1E3A5F]">Total du marché {fmtEur(lot.marche_total_ht)}</div>
                    </>
                  )}
                </div>
              )}
            </div>

            <ol className="flex items-start overflow-x-auto px-2 py-3" aria-label={`Étapes du lot ${numero || lot.nom}`}>
              {lot.etapes.map((e) => (
                <Etape key={e.cle} etape={e} courante={e.cle === lot.etape_courante && e.etat !== 'fait'}
                  onClic={canWrite && !lot.virtuel && e.cle === 'signe' && e.etat !== 'fait'
                    && ['attribue', 'en_cours', 'receptionne'].includes(lot.statut) ? () => setSigneCible(lot) : null} />
              ))}
            </ol>

            <div className="flex flex-wrap items-center gap-2 px-4 pb-3">
              {actionsLot?.(lot)}
              {canWrite && lot.sans_acces && (
                <Button size="sm" variant="outline" className="rounded-none text-xs gap-1 border-[#B45309] text-[#B45309]"
                  onClick={() => { setInvCible(lot); setInvEmail(''); setInvResultat(null); }} data-testid={`frise-inviter-${lot.id}`}>
                  <UserPlus size={12} /> Inviter l'entreprise
                </Button>
              )}
              {aDesOffres && !lot.offres_masquees && (
                <>
                  <Button size="sm" variant="outline"
                    className="rounded-none text-xs gap-1 border-[#0369A1] text-[#0369A1]"
                    onClick={() => setComparatif({ dceId: lot.dce_id, dceStatut: lot.dce_statut, lotId: lot.dpgf_lot_id })}
                    data-testid={`frise-comparatif-${lot.id}`}>
                    <Trophy size={12} /> {estAmo ? 'Rapport et mon avis' : 'Comparatif des offres'}
                  </Button>
                  <a
                    href={`${API_URL}/api/chantiers/${chantierId}/dce/${lot.dce_id}/comparatif/export-pdf`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 border border-[#C75B39] text-[#C75B39] hover:bg-[#FFF3E0]"
                    data-testid={`frise-rapport-${lot.id}`}
                  >
                    <FilePdf size={12} /> Rapport d'analyse
                  </a>
                </>
              )}
              {canDemanderAvis && aDesOffres && lot.nb_offres > 0 && (
                <Button size="sm" variant="outline" className="rounded-none text-xs gap-1 border-[#7B5EA7] text-[#7B5EA7]"
                  onClick={() => setAvisCible(lot)} data-testid={`frise-demande-avis-${lot.id}`}>
                  <Megaphone size={12} /> Demander l'avis de l'AMO
                </Button>
              )}
              {(lot.avis?.demande_le || lot.avis?.nb_avis > 0) && (
                <span className="text-[11px] text-[#4B3A6B]" data-testid={`frise-avis-${lot.id}`}>
                  {lot.avis.demande_le && `Avis de l'AMO demandé le ${fmtDate(lot.avis.demande_le)}`}
                  {lot.avis.demande_le && lot.avis.nb_avis > 0 && ' · '}
                  {lot.avis.nb_avis > 0 && `${lot.avis.nb_avis} avis reçu${lot.avis.nb_avis > 1 ? 's' : ''}`}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {/* Comparatif et rapport d'analyse, avant et après l'attribution (remplace CSL-10) */}
      <DCEComparatifDialog
        chantierId={chantierId}
        dceId={comparatif?.dceId}
        dceStatut={comparatif?.dceStatut}
        focusLotId={comparatif?.lotId}
        open={!!comparatif}
        onOpenChange={(o) => { if (!o) setComparatif(null); }}
        onAttribue={() => { load(); onChanged?.(); }}
      />

      {/* B6 (P2#20) — étape « Signé » : déposer le marché signé ou l'envoyer à la signature */}
      {signeCible && (
        <MarcheSigneDialog chantierId={chantierId} lot={signeCible} onClose={() => setSigneCible(null)}
          onFait={() => { setSigneCible(null); load(); onChanged?.(); }} />
      )}

      {/* A6 (P3#10) — inviter le titulaire : compte entreprise + lien d'invitation + ajout au chantier */}
      <Dialog open={!!invCible} onOpenChange={(o) => { if (!o) setInvCible(null); }}>
        <DialogContent className="max-w-lg" data-testid="inviter-entreprise-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Inviter l'entreprise {invCible?.entreprise_nom}</DialogTitle>
          </DialogHeader>
          {invResultat ? (
            <div className="space-y-3 text-sm">
              <p>{invResultat.message}</p>
              {invResultat.activation_url && (
                <Input readOnly value={invResultat.activation_url} className="rounded-none text-xs" onFocus={(e) => e.target.select()} />
              )}
              <div className="flex justify-end gap-2">
                {invResultat.activation_url && (
                  <Button variant="outline" className="rounded-none" onClick={async () => {
                    try { await navigator.clipboard.writeText(invResultat.activation_url); toast.success('Lien copié'); } catch { toast.info('Sélectionnez le lien et copiez-le'); }
                  }}>Copier le lien</Button>
                )}
                <Button className="rounded-none" onClick={() => setInvCible(null)}>Fermer</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#6B6B6B]">
                L'entreprise titulaire de <strong>{libNumero(invCible?.numero)} {invCible?.nom}</strong> reçoit un lien pour créer son
                accès : elle verra son marché, ses actions et ses réserves. Elle est ajoutée à l'équipe du projet.
              </p>
              <Input type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)}
                placeholder="E-mail de l'entreprise (si absent de l'annuaire)" className="rounded-none" data-testid="inviter-entreprise-email" />
              <div className="flex justify-end gap-2">
                <Button variant="outline" className="rounded-none" onClick={() => setInvCible(null)}>Annuler</Button>
                <Button className="rounded-none bg-[#B45309] hover:bg-[#92400E] text-white" disabled={envoi}
                  onClick={inviterEntreprise} data-testid="inviter-entreprise-envoyer">Inviter l'entreprise</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!avisCible} onOpenChange={(o) => { if (!o) setAvisCible(null); }}>
        <DialogContent className="max-w-lg" data-testid="demande-avis-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Demander l'avis de l'AMO</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B6B6B]">
            L'AMO du chantier est prévenu (la demande apparaît dans ses actions) et lit le rapport d'analyse des offres du lot
            <strong> {libNumero(avisCible?.numero)} {avisCible?.nom}</strong>. Son avis s'affichera ici et dans le rapport,
            avant votre décision d'attribution. Il voit les prix à partir de la date limite de remise des offres.
          </p>
          <Textarea rows={3} value={avisMessage} onChange={(e) => setAvisMessage(e.target.value)}
            placeholder="Message pour l'AMO (facultatif)" className="rounded-none" data-testid="demande-avis-message" />
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setAvisCible(null)}>Annuler</Button>
            <Button className="rounded-none bg-[#7B5EA7] hover:bg-[#5e4585] text-white" disabled={envoi}
              onClick={envoyerDemandeAvis} data-testid="demande-avis-envoyer">
              Envoyer la demande
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
